import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { EnvironmentService } from '../shared/environment.service';
import { FirebaseAuthService } from '../shared/firebase-auth.service';
import { ClientLoggingService } from '../shared/client-logging.service';
import {
  ATTACHMENT_LIMITS,
  AttachmentUrlResponse,
  ChatAttachment,
  CreateAttachmentResponse,
  PASTED_IMAGE_LABEL,
  toChatAttachment,
} from '../models/chat.models';

/**
 * Uploads and manages the images a user attaches to a chat message (Issue #1746).
 *
 * The service owns one draft list — the images sitting in the composer that have not been sent yet
 * — and the three-step upload each of them goes through. Step one asks the backend to stage the
 * attachment, which returns a signed URL that permits a write to exactly one object in a private
 * bucket and expires within minutes. Step two PUTs the raw bytes to that URL, so the image never
 * passes through the application API at all. Step three asks the backend to finalise the draft,
 * which is where the file signature is proved, the pixel budget is enforced, and camera and
 * location metadata are stripped.
 *
 * Two rules shape the rest of the class. Nothing is ever marked ready optimistically: an attachment
 * reaches `ready` only when the backend says its own validation passed, so a file that fails at step
 * three shows a failure rather than looking attached. And every browser-local object URL created for
 * a thumbnail is revoked when the attachment is removed or the draft is cleared, so a removed image
 * leaves no blob behind in the tab.
 */
@Injectable({ providedIn: 'root' })
export class ChatAttachmentService {
  private http = inject(HttpClient);
  private env = inject(EnvironmentService);
  private auth = inject(FirebaseAuthService);

  /** The signed-in user the current drafts belong to, so an identity change can be detected. */
  private draftsOwner: string | null = null;

  private readonly apiBase: string;

  /** Draft attachments currently shown in the composer, in the order the user added them. */
  private drafts: ChatAttachment[] = [];

  /** Aborts an in-flight upload when the user cancels or removes the attachment. */
  private uploadAborts = new Map<string, AbortController>();

  /** Browser-local object URLs created for draft thumbnails, revoked on removal. */
  private objectUrls = new Map<string, string>();

  /**
   * Signed read URLs for sent attachments, cached until shortly before they expire so that
   * re-rendering a message does not mint a fresh URL for every thumbnail.
   */
  private signedUrlCache = new Map<string, { url: string; expiresAtMs: number }>();

  private draftsChanged = new Subject<ChatAttachment[]>();
  /** Emits the current draft list whenever it changes. */
  readonly drafts$ = this.draftsChanged.asObservable();

  private announcements = new Subject<string>();
  /**
   * Emits messages intended for a screen reader — an image being added, removed, or refused.
   * Pasting in particular has no visual moment of its own, so without this a blind user would have
   * no way of knowing an attachment appeared.
   */
  readonly announcements$ = this.announcements.asObservable();

  constructor() {
    this.apiBase = `https://${this.env.getApiServer()}/v1/chat`;

    // Drafts belong to a person, and this service is provided in root, so it outlives the composer
    // and every component that shows it. Signing out does not reload the page — it routes to the
    // signed-out view — so without this the drafts of whoever was here before would still be in
    // memory when the next person signs in, and the composer repopulates from this service on init.
    // Switching accounts is a first-class flow, so that is not a hypothetical: the incoming user
    // would see the previous user's file names. They could not send them, because the backend
    // checks ownership on every read, but the names alone are user content and should not appear.
    this.auth.user$.subscribe(user => {
      const owner = user?.email ?? null;
      if (owner !== this.draftsOwner) {
        this.discardDraftsForIdentityChange();
        this.draftsOwner = owner;
      }
    });
  }

  /**
   * Drops draft state when the signed-in identity changes.
   *
   * <p>Local state only. The staged records are deliberately not deleted here: by the time an
   * identity change is observed there may be no valid credential to authorise a delete, and a
   * request that fails is worse than one that was never made because it looks like cleanup that
   * happened. The staged objects are reclaimed server-side once they pass the staleness grace,
   * which is the mechanism that holds regardless of what any browser does.
   */
  private discardDraftsForIdentityChange(): void {
    if (this.drafts.length === 0 && this.objectUrls.size === 0) {
      return;
    }
    for (const attachment of this.drafts) {
      this.uploadAborts.get(attachment.attachmentId)?.abort();
      this.releaseObjectUrl(attachment.attachmentId);
    }
    this.uploadAborts.clear();
    this.signedUrlCache.clear();
    this.drafts = [];
    this.emitDrafts();
  }

  /** Current draft attachments, newest last. */
  get currentDrafts(): ChatAttachment[] {
    return [...this.drafts];
  }

  /** Whether another image may still be added to this message. */
  get canAddMore(): boolean {
    return this.drafts.length < ATTACHMENT_LIMITS.maxAttachmentsPerMessage;
  }

  /**
   * Checks a file against the limits the backend enforces, so an obviously unusable file is
   * refused instantly instead of after a long upload. This is a courtesy, not a security control:
   * the backend re-checks everything against the actual bytes.
   *
   * @returns null when the file is acceptable, or a plain-language reason when it is not
   */
  validateBeforeUpload(file: File): string | null {
    const mimeType = (file.type || '').toLowerCase();
    if (!ATTACHMENT_LIMITS.supportedMimeTypes.includes(mimeType as never)) {
      return `${file.name || 'This file'} is not a PNG or JPEG image. Chat attachments must be PNG or JPEG.`;
    }
    if (file.size > ATTACHMENT_LIMITS.maxBytesPerFile) {
      const limitMb = ATTACHMENT_LIMITS.maxBytesPerFile / (1024 * 1024);
      return `${file.name || 'This image'} is larger than the ${limitMb} MB limit for chat attachments.`;
    }
    if (file.size === 0) {
      return `${file.name || 'This file'} is empty.`;
    }
    return null;
  }

  /**
   * Adds files to the draft and uploads them.
   *
   * Files beyond the per-message limit are refused with an explanation rather than dropped, and
   * each file is validated locally before any network call. Uploads run concurrently because a user
   * who drops four screenshots at once should not wait for them in series.
   *
   * @param files the files chosen, dropped, or pasted
   * @param projectId optional project the conversation belongs to, recorded for scoping and cleanup
   * @param fromClipboard whether these arrived through a paste, which changes the label and the
   *     screen-reader announcement
   */
  async addFiles(files: File[], projectId?: string, fromClipboard = false): Promise<void> {
    const accepted: File[] = [];
    for (const file of files) {
      if (this.drafts.length + accepted.length >= ATTACHMENT_LIMITS.maxAttachmentsPerMessage) {
        // Refusing over the cap goes through the same placeholder the unsupported-type and
        // oversize paths use. Announcing alone was not enough: the announcement is published to a
        // visually hidden live region, so a sighted user who dropped a fifth image saw the image
        // simply not appear, with nothing anywhere on screen saying why. The cap itself was
        // correct; only the explanation was missing, and a refusal a user cannot perceive is
        // indistinguishable from the feature being broken.
        const reason = `${file.name || 'This image'} was not added. A message can carry at most ${
          ATTACHMENT_LIMITS.maxAttachmentsPerMessage
        } images.`;
        this.announce(reason);
        this.pushDraft(this.failedPlaceholder(file, reason, fromClipboard));
        // Every refused file is accounted for individually rather than stopping at the first one.
        // Breaking here dropped the remainder of a multi-file selection without so much as an
        // announcement, so the second and later refusals were silent even to a screen reader.
        continue;
      }
      const problem = this.validateBeforeUpload(file);
      if (problem) {
        this.announce(problem);
        this.pushDraft(this.failedPlaceholder(file, problem, fromClipboard));
        continue;
      }
      accepted.push(file);
    }

    if (accepted.length === 0) return;

    this.announce(
      accepted.length === 1
        ? `${fromClipboard ? 'Image pasted' : 'Image attached'}: ${this.labelFor(accepted[0], fromClipboard)}. Uploading.`
        : `${accepted.length} images ${fromClipboard ? 'pasted' : 'attached'}. Uploading.`
    );

    await Promise.all(accepted.map(file => this.uploadOne(file, projectId, fromClipboard)));
  }

  /**
   * Adds an attachment the backend has already produced and validated to the draft (Issue #1747).
   *
   * A Preview region crop arrives here READY: the server rendered it from the page PDF and stored it
   * under the user's ownership, so there is nothing to upload. It joins the same draft list as an
   * uploaded image, which means the same per-message limit, the same remove button, and the same
   * send path that carries only its ID.
   *
   * @returns null when added, or a plain-language reason when the draft is already full
   */
  addReadyAttachment(attachment: ChatAttachment): string | null {
    if (this.drafts.some(a => a.attachmentId === attachment.attachmentId)) {
      return null;
    }
    if (!this.canAddMore) {
      const reason = `${this.describe(attachment)} was not added. A message can carry at most ${
        ATTACHMENT_LIMITS.maxAttachmentsPerMessage
      } images.`;
      this.announce(reason);
      return reason;
    }
    const { previewUrl, progress, ...ready } = attachment;
    this.pushDraft({ ...ready, state: 'ready' });
    this.announce(`${this.describe(attachment)} attached. It is ready to send.`);
    return null;
  }

  /**
   * Removes one draft attachment, cancelling its upload if it is still in flight and asking the
   * backend to delete the stored object so an abandoned image does not linger.
   */
  async remove(attachmentId: string): Promise<void> {
    const attachment = this.drafts.find(a => a.attachmentId === attachmentId);
    this.uploadAborts.get(attachmentId)?.abort();
    this.uploadAborts.delete(attachmentId);
    this.releaseObjectUrl(attachmentId);
    this.drafts = this.drafts.filter(a => a.attachmentId !== attachmentId);
    this.emitDrafts();
    this.announce(`Removed ${attachment ? this.describe(attachment) : 'the image'}.`);

    if (attachment && !attachmentId.startsWith('local-')) {
      try {
        await firstValueFrom(this.http.delete(`${this.apiBase}/attachments/${attachmentId}`));
      } catch (error) {
        // The stored object is swept by the abandoned-draft cleanup anyway, so a failed delete is
        // not worth interrupting the user over.
        ClientLoggingService.log('WARN', '[ChatAttachments] Could not delete attachment on removal');
      }
    }
  }

  /**
   * Takes the draft attachments out of the composer and hands them to the caller.
   *
   * This is what makes a sent message immutable. The composer's draft list is emptied at the moment
   * of sending, so adding or removing another image afterwards changes the next message and cannot
   * reach back into one that is queued, retrying, or already sent.
   */
  takeReadyDrafts(): ChatAttachment[] {
    const ready = this.drafts
      .filter(a => a.state === 'ready')
      // The browser-local object URL is deliberately dropped rather than handed to the sent
      // message. Passing it along would leave a blob alive in the tab with nothing responsible for
      // revoking it, and it would also mean a message rendered from a local blob before a reload
      // and from an authorised signed URL after one. Dropping it here makes a sent message always
      // render the same way, through the short-lived signed URL that re-checks ownership.
      .map(({ previewUrl, progress, ...attachment }) => attachment);

    for (const attachment of this.drafts) {
      this.releaseObjectUrl(attachment.attachmentId);
    }
    this.drafts = [];
    this.emitDrafts();
    return ready;
  }

  /**
   * Discards every draft, releasing both its browser-local object URL and the staged record.
   *
   * <p>This is the release path for drafts a user abandons, and it is deliberately not wired to
   * component teardown. The composer sits inside a drawer that keeps its content mounted, so
   * teardown effectively never happens; and the drafts are application-scoped rather than owned by
   * the composer, so clearing them when one component goes away would discard images the user still
   * has staged.
   *
   * <p><b>This method currently has no production caller, and that is deliberate rather than an
   * oversight.</b> #1769 decides when a staged image counts as abandoned — closing the chat panel
   * is the obvious candidate — and will wire this method to whichever gesture is chosen. It is a
   * product decision because it changes user-visible behaviour: staged images survive a close and
   * reopen today, so releasing on close would destroy work after a mis-click, with no undo. The
   * behaviour here is correct and covered by tests; only its trigger is undecided, so deleting it
   * would mean rebuilding and re-testing identical code once #1769 lands.
   *
   * <p>Meanwhile the guarantee does not rest here. Drafts are released on explicit removal and on
   * send, and anything else is reclaimed server-side once it passes the staleness grace — which is
   * the mechanism that actually holds, because a browser cannot be relied on to clean up after
   * itself.
   */
  clearDrafts(): void {
    for (const attachment of this.drafts) {
      this.uploadAborts.get(attachment.attachmentId)?.abort();
      this.releaseObjectUrl(attachment.attachmentId);
      // Release the staged object as well, not just the browser-local state. Dropping the local
      // array alone leaves the record staged server-side, where it counts against the user's
      // unsent-draft quota until the daily sweep collects it — so closing the chat panel with an
      // unsent image used to make the next few attachments quietly more expensive, and eventually
      // impossible. This method previously fixed a blob-URL leak while creating a server-side one.
      this.releaseStagedAttachment(attachment.attachmentId);
    }
    this.uploadAborts.clear();
    this.drafts = [];
    this.emitDrafts();
  }

  /**
   * Asks the backend to drop a staged attachment, without waiting for the answer.
   *
   * <p>Used on teardown, where there is no user to report a failure to and no next step that
   * depends on it. A draft whose delete does not land is collected by the retention sweep anyway,
   * so the cost of failure is a delay rather than a leak.
   */
  private releaseStagedAttachment(attachmentId: string): void {
    if (attachmentId.startsWith('local-')) {
      // Never reached the backend, so there is nothing staged to release.
      return;
    }
    this.http.delete(`${this.apiBase}/attachments/${attachmentId}`).subscribe({
      error: () => ClientLoggingService.log('WARN', '[ChatAttachments] Could not release a staged attachment'),
    });
  }

  /**
   * Returns a short-lived signed URL for viewing a sent attachment.
   *
   * The URL is requested only when a thumbnail is actually about to be shown, is authorised
   * server-side before it is minted, and is cached only until shortly before it expires.
   */
  async viewUrl(attachmentId: string): Promise<string | null> {
    const cached = this.signedUrlCache.get(attachmentId);
    if (cached && cached.expiresAtMs > Date.now() + 30_000) {
      return cached.url;
    }
    try {
      const response = await firstValueFrom(
        this.http.get<AttachmentUrlResponse>(`${this.apiBase}/attachments/${attachmentId}/url`)
      );
      if (!response?.url) return null;
      const expiresAtMs = this.toEpochMs(response.expiresAt);
      this.signedUrlCache.set(attachmentId, { url: response.url, expiresAtMs });
      return response.url;
    } catch {
      // A failed lookup means the object is gone or was never ours; the caller shows the
      // unavailable state rather than a broken image.
      this.signedUrlCache.delete(attachmentId);
      return null;
    }
  }

  /** Forgets every cached signed URL. Called on sign-out and session teardown. */
  clearUrlCache(): void {
    this.signedUrlCache.clear();
  }

  /**
   * Extracts image files from a clipboard event.
   *
   * The clipboard can hold an image and text at the same time — copying a region out of a document
   * viewer typically produces both. Returning only the images here lets the caller attach them
   * while leaving the browser's own text paste untouched, so both halves survive.
   */
  static imagesFromClipboard(event: ClipboardEvent): File[] {
    const items = event.clipboardData?.items;
    if (!items) return [];
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind !== 'file' || !item.type.startsWith('image/')) continue;
      const file = item.getAsFile();
      if (file) files.push(file);
    }
    return files;
  }

  /** Whether a drag event is carrying files, as opposed to selected text or a link. */
  static dragCarriesFiles(event: DragEvent): boolean {
    const types = event.dataTransfer?.types;
    if (!types) return false;
    return Array.from(types).includes('Files');
  }

  // ─── internals ──────────────────────────────────────────────────────

  private async uploadOne(file: File, projectId: string | undefined, fromClipboard: boolean): Promise<void> {
    const label = this.labelFor(file, fromClipboard);
    const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    const draft: ChatAttachment = {
      attachmentId: localId,
      mimeType: file.type,
      byteSize: file.size,
      width: 0,
      height: 0,
      displayName: label,
      state: 'uploading',
      progress: 0,
      previewUrl: this.createObjectUrl(localId, file),
    };
    this.pushDraft(draft);

    let staged: CreateAttachmentResponse;
    try {
      staged = await firstValueFrom(
        this.http.post<CreateAttachmentResponse>(`${this.apiBase}/attachments`, {
          mimeType: file.type,
          displayName: fromClipboard ? '' : file.name,
          byteSize: String(file.size),
          projectId: projectId ?? '',
        })
      );
    } catch (error) {
      this.failDraft(localId, this.readableError(error, 'The image could not be prepared for upload.'));
      return;
    }

    const attachmentId = staged.attachment.attachmentId;
    // The draft keeps its browser-local preview but adopts the server-issued identity, so a later
    // removal or send refers to the same attachment the backend knows about.
    this.reidentifyDraft(localId, attachmentId);

    const abort = new AbortController();
    this.uploadAborts.set(attachmentId, abort);

    try {
      await this.putBytes(staged.uploadUrl, staged.uploadContentType, file, abort.signal, percent =>
        this.updateDraft(attachmentId, { progress: percent })
      );
    } catch (error) {
      this.uploadAborts.delete(attachmentId);
      if (abort.signal.aborted) return; // The user cancelled; the draft is already gone.
      this.failDraft(attachmentId, 'The image could not be uploaded. Check your connection and try again.');
      return;
    }
    this.uploadAborts.delete(attachmentId);

    this.updateDraft(attachmentId, { state: 'validating', progress: 100 });

    try {
      const finalized = await firstValueFrom(
        this.http.post<Record<string, unknown>>(`${this.apiBase}/attachments/${attachmentId}/finalize`, {})
      );
      const validated = toChatAttachment(finalized as never);
      this.updateDraft(attachmentId, {
        state: validated.state,
        mimeType: validated.mimeType || file.type,
        byteSize: validated.byteSize || file.size,
        width: validated.width,
        height: validated.height,
        displayName: validated.displayName || label,
        failureReason: validated.failureReason,
        progress: undefined,
      });
      if (validated.state === 'ready') {
        this.announce(`${label} is ready to send.`);
      } else {
        this.announce(validated.failureReason ?? `${label} could not be attached.`);
      }
    } catch (error) {
      const reason = this.readableError(error, 'This image could not be validated and was not attached.');
      this.failDraft(attachmentId, reason);
    }
  }

  /**
   * PUTs the file to the signed URL, reporting progress.
   *
   * XMLHttpRequest is used rather than HttpClient because the signed URL must be called with
   * exactly the headers it was signed for; Angular's interceptors would otherwise attach the
   * application's Firebase authorization header, which Cloud Storage rejects as an unexpected
   * signed header.
   */
  private putBytes(
    url: string,
    contentType: string,
    file: File,
    signal: AbortSignal,
    onProgress: (percent: number) => void
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open('PUT', url, true);
      request.setRequestHeader('Content-Type', contentType);
      request.upload.onprogress = event => {
        if (event.lengthComputable) {
          onProgress(Math.min(99, Math.round((event.loaded / event.total) * 100)));
        }
      };
      request.onload = () =>
        request.status >= 200 && request.status < 300
          ? resolve()
          : reject(new Error(`Upload failed with status ${request.status}`));
      request.onerror = () => reject(new Error('Upload failed'));
      request.onabort = () => reject(new Error('Upload cancelled'));
      signal.addEventListener('abort', () => request.abort(), { once: true });
      request.send(file);
    });
  }

  private labelFor(file: File, fromClipboard: boolean): string {
    if (fromClipboard || !file.name) return PASTED_IMAGE_LABEL;
    return file.name;
  }

  private describe(attachment: ChatAttachment): string {
    return attachment.displayName || PASTED_IMAGE_LABEL;
  }

  private failedPlaceholder(file: File, reason: string, fromClipboard: boolean): ChatAttachment {
    return {
      attachmentId: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      mimeType: file.type,
      byteSize: file.size,
      width: 0,
      height: 0,
      displayName: this.labelFor(file, fromClipboard),
      state: 'failed',
      failureReason: reason,
    };
  }

  private pushDraft(attachment: ChatAttachment): void {
    this.drafts = [...this.drafts, attachment];
    this.emitDrafts();
  }

  private updateDraft(attachmentId: string, patch: Partial<ChatAttachment>): void {
    this.drafts = this.drafts.map(a => (a.attachmentId === attachmentId ? { ...a, ...patch } : a));
    this.emitDrafts();
  }

  private reidentifyDraft(localId: string, attachmentId: string): void {
    const objectUrl = this.objectUrls.get(localId);
    if (objectUrl) {
      this.objectUrls.delete(localId);
      this.objectUrls.set(attachmentId, objectUrl);
    }
    this.drafts = this.drafts.map(a => (a.attachmentId === localId ? { ...a, attachmentId } : a));
    this.emitDrafts();
  }

  private failDraft(attachmentId: string, reason: string): void {
    this.updateDraft(attachmentId, { state: 'failed', failureReason: reason, progress: undefined });
    this.announce(reason);
  }

  private createObjectUrl(attachmentId: string, file: File): string {
    const url = URL.createObjectURL(file);
    this.objectUrls.set(attachmentId, url);
    return url;
  }

  private releaseObjectUrl(attachmentId: string): void {
    const url = this.objectUrls.get(attachmentId);
    if (url) {
      URL.revokeObjectURL(url);
      this.objectUrls.delete(attachmentId);
    }
  }

  private emitDrafts(): void {
    this.draftsChanged.next([...this.drafts]);
  }

  private announce(message: string): void {
    this.announcements.next(message);
  }

  private toEpochMs(expiresAt: string | number | undefined): number {
    if (expiresAt == null) return Date.now() + 60_000;
    const seconds = typeof expiresAt === 'string' ? parseInt(expiresAt, 10) : expiresAt;
    return Number.isFinite(seconds) ? seconds * 1000 : Date.now() + 60_000;
  }

  /**
   * Turns a transport failure into something worth showing a person.
   *
   * The backend writes its rejection messages for exactly this purpose — "this file was sent as
   * image/png but its contents are image/jpeg" is more useful than "400" — so when one is present
   * it is preferred over the generic fallback.
   */
  private readableError(error: unknown, fallback: string): string {
    if (error instanceof HttpErrorResponse) {
      const message = (error.error as { message?: string } | undefined)?.message;
      if (message) return message;
      if (error.status === 429) return 'Too many uploads in a short time. Please wait a moment and try again.';
      if (error.status === 0) return 'The image could not be uploaded. Check your connection and try again.';
    }
    return fallback;
  }
}
